import React from "react"
import { PlusOutlined } from "@ant-design/icons"
import {
  Card,
  Divider,
  Image,
  Input,
  notification,
  Spin,
  Switch,
  Upload,
} from "antd"
import { Header } from "src/components/layout/header"
import { Button } from "src/components/actions/button"
import { SaveSvg } from "src/components/Svg"
import { usePermission } from "src/hooks"
import api from "src/api"
import { useUploadFile } from "src/hooks/use-upload-file"
import { API_BASE_MEDIA_URL } from "src/config"
import styled from "styled-components"

export default function SystemSettings() {
  const { hasPermission } = usePermission()
  const canEdit = hasPermission("system-settings.edit")
  const [settings, setSettings] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  const [previewImage, setPreviewImage] = React.useState(null)

  React.useEffect(() => {
    loadSettings()
  }, [])

  async function loadSettings() {
    setIsLoading(true)
    try {
      const { data } = await api.get("system-settings", { useV2Api: true })
      setSettings(data.items ?? [])
    } catch (error) {
      console.error("Ошибка загрузки настроек", error)
      notification.error({ message: "Ошибка загрузки настроек" })
    } finally {
      setIsLoading(false)
    }
  }

  function updateSetting(id, field, value) {
    setSettings((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    )
  }
  const [uploadingById, setUploadingById] = React.useState({})

  function isPromoBannerImageSetting(setting) {
    const normalized = [
      setting?.title,
      setting?.description,
      setting?.key,
      setting?.name,
      setting?.code,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()

    return (
      setting.type === "string" &&
      (normalized.includes("promo") || normalized.includes("промо")) &&
      (normalized.includes("banner") || normalized.includes("баннер")) &&
      (normalized.includes("изображ") || normalized.includes("image"))
    )
  }

  function getImageFileList(url) {
    if (!url) return []

    return [
      {
        uid: String(url),
        name: "promo-banner",
        status: "done",
        url,
      },
    ]
  }

  function buildUploadedFileUrl(uploadedFile) {
    if (!uploadedFile?.path || !uploadedFile?.systemName) return ""

    const normalizedBase = String(API_BASE_MEDIA_URL || "").replace(/\/+$/, "")
    const normalizedPath = String(uploadedFile.path).replace(/^\/+/, "")
    const normalizedSystemName = String(uploadedFile.systemName).replace(/^\/+/, "")

    return `${normalizedBase}/${normalizedPath}/${normalizedSystemName}`
  }

  async function handlePromoBannerUpload(settingId, file) {
    setUploadingById((prev) => ({ ...prev, [settingId]: true }))
    try {
      const uploadedFile = await useUploadFile(file)
      if (!uploadedFile?.id) {
        return false
      }

      const imageUrl = buildUploadedFileUrl(uploadedFile)
      if (!imageUrl) {
        notification.error({ message: "Не удалось сформировать URL изображения" })
        return false
      }

      updateSetting(settingId, "value", imageUrl)
      notification.success({ message: "Изображение загружено" })
    } finally {
      setUploadingById((prev) => ({ ...prev, [settingId]: false }))
    }

    return false
  }

  async function onPreview(file) {
    const preview = file.originFileObj ?? file
    if (preview instanceof File) {
      preview.url = await URL.createObjectURL(preview)
    }
    setPreviewImage(preview)
  }

  async function handleSave() {
    setIsSaving(true)
    try {
      const payload = settings.map((s) => ({
        id: s.id,
        is_enabled: s.is_enabled,
        value: s.value,
      }))
      await api.put("system-settings-batch", { settings: payload }, { useV2Api: true })
      notification.success({ message: "Настройки сохранены" })
    } catch (error) {
      console.error("Ошибка сохранения", error)
      notification.error({ message: "Ошибка сохранения" })
    } finally {
      setIsSaving(false)
    }
  }

  const groups = settings.reduce((acc, s) => {
    const group = s.group || "general"
    if (!acc[group]) acc[group] = []
    acc[group].push(s)
    return acc
  }, {})

  const groupTitles = {
    general: "Общие",
    mobile: "Мобильное приложение",
    packaging: "Упаковка",
  }

  if (isLoading) {
    return (
      <>
        <Header title="Системные настройки" />
        <SpinWrapper><Spin size="large" /></SpinWrapper>
      </>
    )
  }

  return (
    <>
      <Header
        title="Системные настройки"
        extra={
          canEdit && (
            <Button
              type="primary"
              icon={<SaveSvg />}
              onClick={handleSave}
              loading={isSaving}
            >
              Сохранить все
            </Button>
          )
        }
      />

      {Object.entries(groups).map(([group, items]) => (
        <StyledCard key={group} title={groupTitles[group] || group}>
          {items.map((setting, idx) => (
            <React.Fragment key={setting.id}>
              {idx > 0 && <Divider style={{ margin: "12px 0" }} />}
              <SettingRow>
                <SettingInfo>
                  <SettingTitle>{setting.title}</SettingTitle>
                  {setting.description && (
                    <SettingDesc>{setting.description}</SettingDesc>
                  )}
                </SettingInfo>
                <SettingControl>
                  {setting.type === "boolean" && (
                    <Switch
                      checked={setting.is_enabled}
                      disabled={!canEdit}
                      onChange={(checked) =>
                        updateSetting(setting.id, "is_enabled", checked)
                      }
                    />
                  )}
                  {isPromoBannerImageSetting(setting) && (
                    <PromoBannerControl>
                      <Upload
                        listType="picture-card"
                        accept="image/jpeg,image/png,image/jpg"
                        maxCount={1}
                        beforeUpload={(file) =>
                          handlePromoBannerUpload(setting.id, file)
                        }
                        fileList={getImageFileList(setting.value)}
                        onPreview={onPreview}
                        onRemove={() => {
                          updateSetting(setting.id, "value", "")
                        }}
                      >
                        {!setting.value && (
                          <div>
                            <PlusOutlined />
                            <div style={{ marginTop: 8 }}>
                              {uploadingById[setting.id]
                                ? "Загрузка..."
                                : "Загрузить изображение"}
                            </div>
                          </div>
                        )}
                      </Upload>

                      <PreviewImage
                        alt={previewImage?.name || "Превью"}
                        preview={{
                          visible: previewImage,
                          onVisibleChange: () => setPreviewImage(null),
                        }}
                        src={previewImage?.url || previewImage?.thumbUrl}
                      />

                    </PromoBannerControl>
                  )}
                  {setting.type === "string" && !isPromoBannerImageSetting(setting) && (
                    <Input
                      value={setting.value ?? ""}
                      disabled={!canEdit}
                      placeholder="Значение"
                      style={{ maxWidth: 400 }}
                      onChange={(e) =>
                        updateSetting(setting.id, "value", e.target.value)
                      }
                    />
                  )}
                  {setting.type === "text" && (
                    <Input.TextArea
                      value={setting.value ?? ""}
                      disabled={!canEdit}
                      placeholder="Значение"
                      rows={2}
                      style={{ maxWidth: 400 }}
                      onChange={(e) =>
                        updateSetting(setting.id, "value", e.target.value)
                      }
                    />
                  )}
                  {setting.type === "number" && (
                    <Input
                      type="number"
                      value={setting.value ?? ""}
                      disabled={!canEdit}
                      placeholder="0"
                      style={{ maxWidth: 200 }}
                      onChange={(e) =>
                        updateSetting(setting.id, "value", e.target.value)
                      }
                    />
                  )}
                </SettingControl>
              </SettingRow>
            </React.Fragment>
          ))}
        </StyledCard>
      ))}
    </>
  )
}

const SpinWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 80px 0;
`

const StyledCard = styled(Card)`
  margin-bottom: 16px;
`

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
`

const SettingInfo = styled.div`
  flex: 1;
`

const SettingTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
`

const SettingDesc = styled.div`
  color: #888;
  font-size: 12px;
  margin-top: 2px;
`

const SettingControl = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`

const PromoBannerControl = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`

const PreviewImage = styled(Image)`
  display: none;
`
